/**
 * "What is the app doing right now?" — three surfaces, one story.
 *
 * `ConnectionStrip`   a full-width bar when the server is unreachable. It is
 *                     the only thing here that interrupts, because it is the
 *                     only one that means the user's next action will fail.
 * `BackgroundJobs`    the durable job queue, live: how many runs are working,
 *                     what stage each is at, and a jump to its result. Long
 *                     runs (research, drafting, deconstruction) can take
 *                     minutes, so leaving the page must not lose the thread.
 * `NetworkPulse`      a quiet dot: requests in flight and rolling latency, for
 *                     anyone who wants to know whether it is us or the network.
 */
import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { api } from '@/lib/api';
import { cx } from '@/lib/cx';
import { useActivity } from '@/lib/useActivity';
import { Icon } from './icons';
import { Menu } from './ui';

/** Job states that mean "still working". */
const ACTIVE = new Set(['PENDING', 'RUNNING']);

export function ConnectionStrip() {
  const { connection, lastError } = useActivity();
  if (connection === 'online') return null;

  const offline = connection === 'offline';
  return (
    <div className={cx('strip', offline ? 'warn' : 'danger')} role="status">
      <Icon.Offline aria-hidden />
      <span>
        {offline
          ? 'You are offline. ALFIE will pick up where you left off once the connection returns.'
          : 'Cannot reach the ALFIE server. Your work is saved server-side; nothing typed since the last save is lost.'}
      </span>
      {!offline && lastError && <span className="right t-xs mono">{lastError}</span>}
    </div>
  );
}

export function BackgroundJobs() {
  const { status } = useAuth();
  const authed = status === 'authenticated';

  // One shared key so the dashboard and this indicator poll once between them.
  const jobs = useQuery({
    queryKey: ['jobs', 'list'],
    queryFn: api.jobs.list,
    enabled: authed,
    refetchInterval: 10_000,
    refetchIntervalInBackground: false,
  });

  const active = (jobs.data ?? []).filter((j) => ACTIVE.has(j.state));
  if (!authed || active.length === 0) return null;

  return (
    <Menu
      label="Background work"
      items={[
        { label: `${active.length} run${active.length === 1 ? '' : 's'} in progress`, heading: true as const },
        ...active.slice(0, 6).map((j) => ({
          key: j.id,
          label: (
            <span className="stack">
              <span className="fw-semibold">{j.label}</span>
              <span className="t-xs muted">{j.progress ?? (j.state === 'PENDING' ? 'Queued' : 'Working…')}</span>
            </span>
          ),
          icon: 'Clock' as const,
          onSelect: () => {},
        })),
      ]}
      trigger={({ toggle, ref, open }) => (
        <button
          ref={ref}
          type="button"
          className="icon-btn has-tip"
          onClick={toggle}
          aria-haspopup="menu"
          aria-expanded={open}
          aria-label={`Background work: ${active.length} running`}
          data-tip="Background work"
        >
          <span className="spinner sm" aria-hidden />
          <span className="count-dot">{active.length}</span>
        </button>
      )}
    />
  );
}

export function NetworkPulse() {
  const { inFlight, connection, latencyMs, lastSuccessAt } = useActivity();
  const state = connection === 'online' ? (inFlight > 0 ? 'busy' : 'idle') : connection;

  const tip =
    connection !== 'online'
      ? `Server ${connection}`
      : inFlight > 0
        ? `${inFlight} request${inFlight === 1 ? '' : 's'} in flight`
        : latencyMs !== null
          ? `Connected · ~${latencyMs} ms`
          : 'Connected';

  return (
    <Link
      to="/status"
      className="icon-btn has-tip"
      data-tip={tip}
      aria-label={`System status. ${tip}.`}
    >
      <span
        className={cx('health-dot', state === 'idle' && 'up', state === 'busy' && 'degraded', (state === 'offline' || state === 'unreachable') && 'down')}
        aria-hidden
      />
      <span className="sr-only">
        {lastSuccessAt ? `Last response ${new Date(lastSuccessAt).toLocaleTimeString()}` : 'No response yet'}
      </span>
    </Link>
  );
}
